import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UsersThree, UserPlus, Trash } from "@phosphor-icons/react";

export default function AdminUsersSection({ currentAdminEmail }) {
  const [admins, setAdmins] = useState([]);
  const [meEmail, setMeEmail] = useState(currentAdminEmail || "");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: list }, meResp] = await Promise.all([
        api.get("/admin/admins"),
        meEmail ? Promise.resolve({ data: { email: meEmail } }) : api.get("/auth/me"),
      ]);
      setAdmins(list);
      if (meResp?.data?.email) setMeEmail(meResp.data.email);
    } catch { /* silently ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const submit = async () => {
    if (!form.email.trim() || !form.first_name.trim() || !form.password.trim()) {
      return toast.error("Email, first name and password are required");
    }
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setSaving(true);
    try {
      await api.post("/admin/admins", form);
      toast.success(`Admin ${form.email} created — they can log in with the password you set`);
      setForm({ email: "", first_name: "", last_name: "", password: "" });
      setCreating(false);
      load();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const del = async (u) => {
    if (u.email === meEmail) return toast.error("You can't remove your own admin account.");
    if (!confirm(`Remove admin access for ${u.email}? They will no longer be able to log in.`)) return;
    try {
      await api.delete(`/admin/admins/${u.id}`);
      toast.success("Admin removed");
      load();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="sticker p-6 mt-6" data-testid="admin-users-card">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 bg-[var(--gc-navy)] text-white flex items-center justify-center border-2 border-[var(--gc-navy)]">
          <UsersThree size={22} weight="bold" />
        </div>
        <h2 className="font-display text-3xl">Admin Users</h2>
      </div>
      <p className="text-sm text-[var(--gc-text-muted)] mb-4">
        Additional team members with full admin access. Each admin can create jobs, edit customers, push proposals, and manage settings. At least one admin must always exist.
      </p>

      <div className="border border-[var(--gc-border)] mb-4">
        <table className="w-full text-sm">
          <thead className="bg-[var(--gc-bg-alt)]">
            <tr className="text-left">
              <th className="px-3 py-2 tech-label">// email</th>
              <th className="px-3 py-2 tech-label">// name</th>
              <th className="px-3 py-2 tech-label">// created</th>
              <th className="px-3 py-2 tech-label text-right">// actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((u) => (
              <tr key={u.id} className="border-t border-[var(--gc-border)]" data-testid={`admin-row-${u.id}`}>
                <td className="px-3 py-2 font-mono-tech">{u.email}</td>
                <td className="px-3 py-2">{u.first_name} {u.last_name}</td>
                <td className="px-3 py-2 text-[var(--gc-text-muted)] text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2 text-right">
                  {u.email === meEmail ? (
                    <span className="chip chip-navy text-[10px]">// you</span>
                  ) : (
                    <button onClick={() => del(u)} className="text-xs px-2 py-1 border border-[var(--gc-danger)] text-[var(--gc-danger)] hover:bg-red-50 font-semibold inline-flex items-center gap-1" data-testid={`admin-remove-${u.id}`}>
                      <Trash size={12} weight="bold"/> Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating ? (
        <div className="border-2 border-[var(--gc-navy)] p-4 bg-[var(--gc-cream,#fdf8f0)]" data-testid="admin-create-form">
          <div className="chip chip-navy mb-3">// new admin</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="tech-label mb-1">First name</div>
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="gc-input w-full" data-testid="new-admin-first-name" />
            </div>
            <div>
              <div className="tech-label mb-1">Last name</div>
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="gc-input w-full" data-testid="new-admin-last-name" />
            </div>
            <div className="md:col-span-2">
              <div className="tech-label mb-1">Email</div>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="new-admin@geekcreative.ie" className="gc-input w-full font-mono-tech" data-testid="new-admin-email" />
            </div>
            <div className="md:col-span-2">
              <div className="tech-label mb-1">Password (min 8 chars)</div>
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="gc-input w-full font-mono-tech" data-testid="new-admin-password" />
              <div className="text-xs text-[var(--gc-text-muted)] mt-1">Share this password with the new admin securely — they can change it later via Forgot Password.</div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={submit} disabled={saving} className="btn-primary px-4 py-2 text-sm disabled:opacity-40" data-testid="save-new-admin">
              {saving ? "Creating…" : "Create Admin"}
            </button>
            <button onClick={() => { setCreating(false); setForm({ email: "", first_name: "", last_name: "", password: "" }); }} className="btn-secondary px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setCreating(true)} className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm" data-testid="add-admin-btn">
          <UserPlus size={14} weight="bold"/> Add Admin
        </button>
      )}
    </div>
  );
}
